import React, { useState, useMemo, useEffect } from "react";
import {
  MotifButton,
  MotifFormField,
  MotifSearch,
  MotifIcon,
  MotifBreadcrumb,
  MotifBreadcrumbItem,
  MotifProgressLoader,
} from "@ey-xd/motif-react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { ApiStatus } from "../../common/services/api-status.enum";
import { AgGridReact } from "ag-grid-react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { deleteIcon, IconoirEditPencil } from "../../common/MotifIcons";
import warningShadowIcon from "../../common/images/Warning-Shadow-icon.svg";
import { CreateDeploymentSchedule } from "./createDeploymentSchedule";
import {
  getDeploymentDetailsData,
  addDeploymentScheduleData,
  updateDeploymentScheduleData,
  deleteDeploymentScheduleData,
} from "./redux/deploymentSchedulerSlice";
import useDeploymentScheduler from "./hooks/useDeploymentScheduler";

import "./deploymentScheduler.scss";

const parseUtcEpochMs = (value) => {
  if (!value) return NaN;
  const raw = String(value).trim();
  const hasTimezone = /Z$|[+-]\d{2}:?\d{2}$/.test(raw) || /GMT|UTC/i.test(raw);
  const iso = hasTimezone ? raw : `${raw}Z`;
  return new Date(iso).getTime();
};

const getDeploymentStatus = (item) => {
  if (!item) return "";
  const endMs = parseUtcEpochMs(item?.outageEndsUtc);
  const isPastEnd = Number.isFinite(endMs) && endMs < Date.now();
  if (item.isEnabled === false && isPastEnd) return "Completed";
  if (item.isEnabled === true) return "Active";
  if (item.isEnabled === false) return "Disabled";
  return item.status || "";
};

const MONTH_SHORT_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const formatDateTimeDisplay = (isoValue) => {
  if (!isoValue) return "";
  const raw = String(isoValue).trim();
  const hasTimezone = /Z$|[+-]\d{2}:?\d{2}$/.test(raw);
  const iso = hasTimezone ? raw : `${raw}Z`;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const monthIndex = date.getMonth();
  const monthName =
    monthIndex >= 0 && monthIndex < 12
      ? MONTH_SHORT_NAMES[monthIndex]
      : String(monthIndex + 1);
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day} ${monthName} ${year}, ${hours}:${minutes}`;
};

const formatUserName = (raw) => {
  if (!raw) return "";
  const trimmed = String(raw).trim();
  if (!trimmed) return "";
  const localPart = trimmed.includes("@") ? trimmed.split("@")[0] : trimmed;
  return localPart
    .split(/[._\-\s]+/)
    .filter(Boolean)
    .map((part) => {
      const cleaned = part.replace(/\d+$/g, "");
      const base = cleaned || part;
      return base.charAt(0).toUpperCase() + base.slice(1);
    })
    .join(" ");
};

const buildUtcDateTime = (dateValue, timeValue, meridiem) => {
  if (!dateValue) return null;
  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;

  let hours = 0;
  let minutes = 0;
  const timeMatch = String(timeValue || "")
    .trim()
    .match(/^(\d{1,2})(?::(\d{1,2}))?$/);
  if (timeMatch) {
    hours = Number(timeMatch[1]);
    const minutesRaw = timeMatch[2];
    if (minutesRaw && minutesRaw.length === 1) {
      minutes = Number(minutesRaw) * 10;
    } else {
      minutes = Number(minutesRaw || 0);
    }
    if (Number.isNaN(hours)) hours = 0;
    if (Number.isNaN(minutes)) minutes = 0;
    const m = String(meridiem || "").toUpperCase();
    if (m === "PM" && hours < 12) hours += 12;
    if (m === "AM" && hours === 12) hours = 0;
  }

  const localDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hours,
    minutes,
    0,
    0
  );
  return localDate.toISOString();
};

const StatusCellRenderer = (params) => {
  const value = params?.value || params?.data?.status || "";
  const statusClass = value.toLowerCase();
  return (
    <span className={`ds-status ds-status-${statusClass}`}>
      • {value}
    </span>
  );
};

const ActionsCellRenderer = (params) => {
  const isCompleted =
    String(params?.data?.status || "").toLowerCase() === "completed";
  const handleEdit = () => {
    if (isCompleted) return;
    if (params?.context?.onEdit) {
      params.context.onEdit(params.data);
    }
  };
  const handleDelete = () => {
    if (params?.context?.onDelete) {
      params.context.onDelete(params.data);
    }
  };
  return (
    <div className="ds-actions-cell">
      <button
        aria-label="Edit deployment"
        type="button"
        onClick={handleEdit}
        className="ds-edit-btn"
        disabled={isCompleted}
      >
        <MotifIcon src={IconoirEditPencil} />
      </button>
      <button
        aria-label="Delete deployment"
        type="button"
        onClick={handleDelete}
        className="ds-delete-btn"
      >
        <MotifIcon src={deleteIcon} />
      </button>
    </div>
  );
};

export const DeploymentScheduler = React.memo(() => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    deploymentDetailsData,
    addDeploymentScheduleData: addState,
    updateDeploymentScheduleData: updateState,
    deleteDeploymentScheduleData: deleteState,
  } = useDeploymentScheduler();
  const isPageLoading =
    deploymentDetailsData?.status === ApiStatus.LOADING ||
    addState?.status === ApiStatus.LOADING ||
    updateState?.status === ApiStatus.LOADING ||
    deleteState?.status === ApiStatus.LOADING;
  const [searchValue, setSearchValue] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);

  useEffect(() => {
    dispatch(getDeploymentDetailsData());
  }, [dispatch]);

  const deployments = useMemo(() => {
    const raw = deploymentDetailsData?.data;
    let list = [];
    if (Array.isArray(raw)) list = raw;
    else if (Array.isArray(raw?.data)) list = raw.data;
    return list.map((item) => ({
      ...item,
      no: item.id,
      deploymentName: item.deploymentName || "",
      startDateTime: formatDateTimeDisplay(item.outageStartsUtc),
      endDateTime: formatDateTimeDisplay(item.outageEndsUtc),
      enabled: Boolean(item.isEnabled),
      lastUpdatedBy: formatUserName(item.lastModifiedBy || item.createdBy || ""),
      lastUpdatedOn: formatDateTimeDisplay(
        item.lastModifiedAtUtc || item.createdAtUtc
      ),
      status: getDeploymentStatus(item),
    }));
  }, [deploymentDetailsData]);

  const onFilterTextBoxChanged = (val) => {
    setSearchValue(val);
  };

  const handleCreateNewSchedule = () => {
    setIsCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedSchedule(null);
  };

  const buildSchedulePayload = (formData) => ({
    deploymentName: formData.deploymentName || "",
    outageStartsUtc: buildUtcDateTime(
      formData.startDate,
      formData.startTime,
      formData.startMeridiem
    ),
    outageEndsUtc: buildUtcDateTime(
      formData.endDate,
      formData.endTime,
      formData.endMeridiem
    ),
    isEnabled: Boolean(formData.enabled),
  });

  const handleSaveNewSchedule = (formData) => {
    if (!formData) return;
    dispatch(addDeploymentScheduleData(buildSchedulePayload(formData)));
  };

  const handleUpdateSchedule = (formData) => {
    if (!formData) return;
    const id =
      selectedSchedule?.id ??
      selectedSchedule?.Id ??
      selectedSchedule?.deploymentId ??
      selectedSchedule?.no;
    if (!id) return;
    dispatch(
      updateDeploymentScheduleData({
        id,
        payload: buildSchedulePayload(formData),
      })
    );
  };

  const handleEdit = (row) => {
    setSelectedSchedule(row);
    setIsEditModalOpen(true);
  };

  const handleDelete = (row) => {
    setScheduleToDelete(row);
    setIsDeleteModalOpen(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setScheduleToDelete(null);
  };

  const handleConfirmDelete = () => {
    const id =
      scheduleToDelete?.id ??
      scheduleToDelete?.Id ??
      scheduleToDelete?.deploymentId ??
      scheduleToDelete?.no;
    if (id) {
      dispatch(deleteDeploymentScheduleData(id));
    }
    setIsDeleteModalOpen(false);
    setScheduleToDelete(null);
  };

  const filteredDeployments = useMemo(() => {
    const term = (searchValue || "").toLowerCase().trim();
    if (!term) return deployments;
    return deployments.filter((d) =>
      Object.values(d).some((v) =>
        String(v || "").toLowerCase().includes(term)
      )
    );
  }, [searchValue, deployments]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      unSortIcon: true,
      sortingOrder: ["asc", "desc", null],
      resizable: false,
      suppressMovable: true,
      suppressMenu: true,
      suppressHeaderMenuButton: true,
      suppressHeaderContextMenu: true,
      cellClass: "ds-cell-left",
    }),
    []
  );

  const numericComparator = (a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (Number.isNaN(na) && Number.isNaN(nb)) return 0;
    if (Number.isNaN(na)) return -1;
    if (Number.isNaN(nb)) return 1;
    return na - nb;
  };

  const isoDateComparator = (isoKey) => (valueA, valueB, nodeA, nodeB) => {
    const rawA = nodeA?.data?.[isoKey];
    const rawB = nodeB?.data?.[isoKey];
    const ta = rawA ? new Date(rawA).getTime() : NaN;
    const tb = rawB ? new Date(rawB).getTime() : NaN;
    if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
    if (Number.isNaN(ta)) return -1;
    if (Number.isNaN(tb)) return 1;
    return ta - tb;
  };

  const columnDefs = [
    {
      field: "no",
      headerName: "No",
      minWidth: 90,
      flex: 0.8,
      tooltipField: "no",
      comparator: numericComparator,
      resizable: true,
    },
    {
      field: "deploymentName",
      headerName: "Deployment Name",
      minWidth: 140,
      flex: 1.2,
      tooltipField: "deploymentName",
      resizable: true,
    },
    {
      field: "startDateTime",
      headerName: "Deployment Start Date & Time",
      minWidth: 180,
      flex: 1.5,
      tooltipField: "startDateTime",
      comparator: isoDateComparator("outageStartsUtc"),
      resizable: true,
    },
    {
      field: "endDateTime",
      headerName: "Deployment End Date & Time",
      minWidth: 180,
      flex: 1.5,
      tooltipField: "endDateTime",
      comparator: isoDateComparator("outageEndsUtc"),
      resizable: true,
    },
    {
      field: "lastUpdatedBy",
      headerName: "Last Updated By",
      minWidth: 130,
      flex: 1,
      tooltipField: "lastUpdatedBy",
      resizable: true,
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 110,
      flex: 0.9,
      cellRenderer: StatusCellRenderer,
      tooltipField: "status",
      resizable: true,
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 90,
      flex: 0.5,
      sortable: false,
      unSortIcon: false,
      cellRenderer: ActionsCellRenderer,
      resizable: true,
    },
  ];

  return (
    <div className="deployment-scheduler-container">
      {isPageLoading && (
        <div className="ds-loader-overlay">
          <MotifProgressLoader show variant="default" />
        </div>
      )}
      <div className="ds-breadcrumb-wrap">
        <MotifBreadcrumb>
          <MotifBreadcrumbItem>
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate("/");
              }}
            >
              Home
            </a>
          </MotifBreadcrumbItem>
          <MotifBreadcrumbItem>
            <a
              href="/settings"
              onClick={(e) => {
                e.preventDefault();
                navigate("/settings");
              }}
            >
              Settings
            </a>
          </MotifBreadcrumbItem>
          <MotifBreadcrumbItem>Deployment Scheduler</MotifBreadcrumbItem>
        </MotifBreadcrumb>
      </div>
      <div className="ds-header">
        <div className="ds-title-wrap">
          <h3 className="ds-title">Deployment Scheduler</h3>
          <p className="ds-subtitle">
            Manage planned deployment windows and maintenance periods for the
            DBS Submission Portal.
          </p>
        </div>
        <div className="ds-header-actions">
          <MotifButton
            onClick={handleCreateNewSchedule}
            size="medium"
            type="button"
            variant="primary"
            className="ds-create-btn"
          >
            + Create New Schedule
          </MotifButton>
        </div>
      </div>

      <div className="ds-card">
        <div className="ds-card-header">
          <div className="ds-card-title-wrap">
            <h4 className="ds-card-title">Current Deployments</h4>
            <p className="ds-card-subtitle">
              Descriptive text that describes your page or content.
            </p>
          </div>
          <MotifFormField className="ds-search-field">
            <MotifSearch
              aria-label="Search"
              onChange={(e) => onFilterTextBoxChanged(e.target?.value)}
              placeholder="Search"
              value={searchValue}
            />
          </MotifFormField>
        </div>
        <div className="ds-table-wrap">
          <div className="ag-theme-quartz ds-grid-theme">
            <AgGridReact
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowData={filteredDeployments}
              domLayout="autoHeight"
              context={{ onEdit: handleEdit, onDelete: handleDelete }}
              suppressCellFocus
              headerHeight={48}
              rowHeight={40}
              suppressDragLeaveHidesColumns
              enableBrowserTooltips
            />
          </div>
        </div>
      </div>

      <CreateDeploymentSchedule
        show={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onSave={handleSaveNewSchedule}
      />
      <CreateDeploymentSchedule
        show={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSave={handleUpdateSchedule}
        mode="edit"
        scheduleId={selectedSchedule?.no || ""}
        initialData={selectedSchedule}
        lastUpdatedBy={selectedSchedule?.lastUpdatedBy || ""}
        lastUpdatedOn={selectedSchedule?.lastUpdatedOn || ""}
      />

      <Dialog
        open={isDeleteModalOpen}
        onClose={handleCancelDelete}
        aria-labelledby="ds-delete-dialog-title"
        aria-describedby="ds-delete-dialog-description"
        className="ds-delete-confirm-dialog"
      >
        <DialogTitle id="ds-delete-dialog-title">
          <IconButton
            onClick={handleCancelDelete}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
          <img src={warningShadowIcon} alt="Warning" />
        </DialogTitle>
        <DialogContent>
          <span id="ds-delete-dialog-description">
            <strong>Delete planned deployment?</strong>
            <p>
              Are you sure you want to delete this planned deployment? This
              action cannot be undone.
            </p>
          </span>
        </DialogContent>
        <DialogActions>
          <MotifButton
            onClick={handleCancelDelete}
            size="medium"
            type="button"
            variant="secondary"
          >
            Cancel
          </MotifButton>
          <MotifButton
            onClick={handleConfirmDelete}
            size="medium"
            type="button"
            variant="warn"
            className="ds-delete-confirm-btn"
          >
            Delete
          </MotifButton>
        </DialogActions>
      </Dialog>
    </div>
  );
});

export default DeploymentScheduler;
